import { Link } from "react-router-dom";
import { ArrowDown, ArrowRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { getHomeV2 } from "@/content/home-v2";
import SolvennLogo from "@/components/solvenn/SolvennLogo";

/** Visual transition + SOLVENN block (layer diagram, problems, products). */
const HomeWhiteLabel = ({ showDivider = true, compact = false }: { showDivider?: boolean; compact?: boolean }): JSX.Element => {
  const { language } = useLanguage();
  const all = getHomeV2(language);
  const d = all.divider;
  const w = all.whiteLabel;
  const p = all.wlProblems;
  const prod = all.wlProducts;
  const integ = all.wlIntegration;

  return (
    <>
      {showDivider && (
      <section className="on-dark-surface relative overflow-hidden bg-[hsl(214_52%_9%)] py-24 text-center">
        <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary to-transparent" aria-hidden="true" />
        <div className="enterprise-container">
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-primary">{d.kicker}</p>
          <h2 className="mx-auto mt-6 max-w-3xl font-display text-3xl font-semibold tracking-tight text-foreground md:text-5xl">
            {d.title} <span className="text-primary">{d.titleAccent}</span>
          </h2>
          <a href="#white-label" className="mt-10 inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm font-semibold text-foreground hover:bg-muted">
            {d.cta} <ArrowDown className="size-4" aria-hidden="true" />
          </a>
        </div>
      </section>
      )}

      <section id="white-label" className="section-padding section-band-base scroll-mt-28">
        <div className="enterprise-container grid gap-12 lg:grid-cols-2 lg:items-center">
          <div>
            <SolvennLogo className="mb-6 h-12 w-auto -ml-2 md:h-14" />
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight text-foreground md:text-5xl">
              {w.title.map((line, i) => (
                <span key={line} className={`block ${i === w.title.length - 1 ? "text-primary" : ""}`}>
                  {line}
                </span>
              ))}
            </h2>
            <p className="mt-6 text-muted-foreground">{w.body}</p>
            <p className="mt-3 text-muted-foreground">{w.body2}</p>
            {compact && (
              <Link to="/white-label" className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-primary hover:underline">
                {d.cta} <ArrowRight className="size-4" aria-hidden="true" />
              </Link>
            )}
          </div>
          <div className="rounded-3xl border border-border bg-card p-6 md:p-8" aria-label={w.layer}>
            <div className="flex flex-wrap justify-center gap-2">
              {w.sources.map((s) => (
                <span key={s} className="rounded-lg border border-border bg-muted px-3 py-2 font-mono text-[11px] uppercase text-muted-foreground">
                  {s}
                </span>
              ))}
            </div>
            <ArrowDown className="mx-auto my-4 size-4 text-primary" aria-hidden="true" />
            <div className="rounded-2xl border border-primary bg-primary/10 px-4 py-5 text-center font-mono text-xs font-semibold uppercase tracking-[0.2em] text-primary">
              {w.layer}
            </div>
            <ArrowDown className="mx-auto my-4 size-4 text-primary" aria-hidden="true" />
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
              {w.products.map((s) => (
                <span key={s} className="rounded-lg border border-border bg-background px-3 py-2 text-center text-xs font-semibold text-foreground">
                  {s}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {!compact && (
      <>
      <section className="section-padding section-band-alt">
        <div className="enterprise-container">
          <h2 className="section-title mb-12 max-w-3xl text-foreground">{p.title}</h2>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2 lg:grid-cols-4">
            {p.items.map((item, i) => (
              <div key={item.title} className="bg-card p-6">
                <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-padding section-band-base">
        <div className="enterprise-container">
          <h2 className="section-title mb-12 max-w-3xl text-foreground">{prod.title}</h2>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {prod.items.map((item) => (
              <div key={item.title} className="rounded-2xl border border-border bg-card p-6">
                <h3 className="font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
          <p className="mt-8 text-sm text-muted-foreground">{prod.note}</p>
        </div>
      </section>

      <section className="section-padding section-band-alt">
        <div className="enterprise-container grid gap-12 lg:grid-cols-[1fr_1.2fr] lg:items-start">
          <div>
            <p className="mb-3 font-mono text-xs uppercase tracking-[0.25em] text-primary">[ {integ.eyebrow} ]</p>
            <h2 className="section-title text-foreground">{integ.title}</h2>
            <p className="mt-6 text-muted-foreground">{integ.body}</p>
            <div className="mt-8 flex flex-wrap gap-2">
              {integ.systems.map((s) => (
                <span key={s} className="rounded-lg border border-border bg-muted px-3 py-2 font-mono text-[11px] uppercase text-muted-foreground">
                  {s}
                </span>
              ))}
            </div>
          </div>
          <div className="grid gap-px overflow-hidden rounded-2xl border border-border bg-border sm:grid-cols-2">
            {integ.items.map((item, i) => (
              <div key={item.title} className="bg-card p-6">
                <span className="font-mono text-[10px] text-muted-foreground">{String(i + 1).padStart(2, "0")}</span>
                <h3 className="mt-3 font-semibold text-foreground">{item.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>
      </>
      )}
    </>
  );
};

export default HomeWhiteLabel;
